/**
 * Subject-line placeholder validation for email templates.
 *
 * Subjects may contain `{{...}}` placeholders that are resolved at send time.
 * Two classes of token exist:
 *   • data placeholders (e.g. {{assessmentName}}) — substituted from the
 *     notification's data payload; must be a known key.
 *   • structural TEXT_* placeholders (e.g. {{TEXT_greeting}}) — reference an
 *     editable text block and are always allowed.
 *
 * This mirrors the backend's placeholder validation so a malformed or unknown
 * placeholder is caught inline before the save round-trip (the backend 400 stays
 * as a fallback).
 */

/**
 * Data placeholders the backend can resolve for any template subject, paired
 * with a human-readable description shown in the placeholder reference panel.
 *
 * This is the single source of truth for the placeholder key list:
 * `KNOWN_PLACEHOLDERS` (used by the validation below and by the info panel) is
 * derived from it, so the keys and their descriptions can never desync.
 */
export interface PlaceholderInfo {
  key: string;
  description: string;
}

export const PLACEHOLDER_INFO: readonly PlaceholderInfo[] = [
  { key: 'supplierName', description: 'Supplier name' },
  { key: 'contractNumber', description: 'Contract number' },
  { key: 'contractName', description: 'Contract name' },
  { key: 'divisionName', description: 'Division name' },
  { key: 'evidenceDueDate', description: 'Evidence due date' },
  { key: 'meetingDate', description: 'Assessment meeting date' },
  { key: 'assessmentName', description: 'Assessment name' },
  { key: 'assessmentId', description: 'Assessment ID' },
  { key: 'assessorName', description: 'Lead assessor name (signs the email)' },
  { key: 'cmNames', description: 'Contract Manager name(s)' },
  { key: 'cmFirstNames', description: 'Contract Manager first name(s) — used in the meeting-invite greeting' },
  { key: 'coNames', description: 'Contract Owner name(s)' },
  { key: 'executiveNames', description: 'Division / Executive leader name(s)' }
] as const;

/** Data placeholders the backend can resolve for any template subject. */
export const KNOWN_PLACEHOLDERS = PLACEHOLDER_INFO.map((p) => p.key);

/** Fast lookup of a curated description by placeholder key. */
const PLACEHOLDER_DESCRIPTIONS: Record<string, string> = PLACEHOLDER_INFO.reduce(
  (acc, { key, description }) => {
    acc[key] = description;
    return acc;
  },
  {} as Record<string, string>
);

// Well-formed placeholder: {{ token }} with no nested braces inside.
const WELL_FORMED_TOKEN = /\{\{[^{}]+\}\}/g;

// Matches an HTML comment, so developer prose inside `<!-- -->` (e.g. mentioning
// `{{like this}}` as an example) is never mistaken for a real placeholder.
const HTML_COMMENT = /<!--[\s\S]*?-->/g;

/** True for data placeholders — structural TEXT_* tokens reference text blocks, not data. */
const isDataToken = (token: string): boolean => !token.startsWith('TEXT_');

/** Extract the trimmed token names inside every well-formed `{{...}}` in a string. */
const extractTokens = (value: string): string[] => {
  const matches = value.match(WELL_FORMED_TOKEN) ?? [];
  return matches.map((token) => token.slice(2, -2).trim());
};

/**
 * Turn a camelCase / snake_case placeholder key into a readable label used when
 * the key has no curated description (e.g. legacy Meetings tokens like
 * `meetingTitle` -> "Meeting Title").
 */
export const humanizePlaceholderKey = (key: string): string => {
  // Acronym boundary (e.g. "IDValue" -> "ID Value"): insert a space before the
  // last uppercase letter of a run of 2+ uppercase letters that's followed by
  // a lowercase letter. A plain loop avoids the overlapping-quantifier ReDoS
  // risk of a regex-only equivalent (`([A-Z]+)(?=[A-Z][a-z])`).
  const isUpper = (ch: string | undefined): boolean => ch !== undefined && ch >= 'A' && ch <= 'Z';
  const isLower = (ch: string | undefined): boolean => ch !== undefined && ch >= 'a' && ch <= 'z';

  let withAcronymBoundary = '';
  for (let i = 0; i < key.length; i++) {
    const boundary = isUpper(key[i]) && isUpper(key[i + 1]) && isLower(key[i + 2]);
    withAcronymBoundary += key[i] + (boundary ? ' ' : '');
  }

  const words = withAcronymBoundary
    .replace(/[_-]+/g, ' ') // snake_case / kebab-case -> spaces
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2') // camelCase boundary
    .trim();
  if (!words) return key;
  return words
    .split(/\s+/)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ');
};

/**
 * Resolve a human-readable description for a placeholder key: the curated
 * PLACEHOLDER_INFO description when present, otherwise a humanized fallback.
 */
export const describePlaceholder = (key: string): string =>
  PLACEHOLDER_DESCRIPTIONS[key] ?? humanizePlaceholderKey(key);

/**
 * Extract the DATA placeholders (non-TEXT_* tokens) used across the given source
 * strings, deduped and in first-seen order. Sources are scanned in the order
 * provided — callers pass subject → text-block defaults → html so the ordering
 * is stable and reflects where the admin is most likely to encounter each token.
 */
export const extractDataPlaceholders = (...sources: (string | null | undefined)[]): string[] => {
  const seen = new Set<string>();
  const ordered: string[] = [];
  for (const source of sources) {
    if (!source) continue;
    for (const token of extractTokens(source.replace(HTML_COMMENT, ''))) {
      if (!isDataToken(token)) continue;
      if (seen.has(token)) continue;
      seen.add(token);
      ordered.push(token);
    }
  }
  return ordered;
};

/**
 * Validate a subject line's placeholders.
 *
 * @param subject       the subject being edited
 * @param defaultSubject the template's default subject; its own data
 *   placeholders are always allowed so switching between the template's valid
 *   tokens never fails.
 * @returns an error message when invalid, otherwise `undefined`.
 */
export const validateSubjectPlaceholders = (
  subject: string,
  defaultSubject: string | null | undefined
): string | undefined => {
  // A subject is required. Check this first so an empty/whitespace subject
  // surfaces a clear inline error (and disables Save / gates the preview off)
  // rather than a confusing placeholder message. Mirrors the backend
  // subjectSchema `.trim().min(1)`.
  if (subject.trim().length === 0) {
    return 'Subject is required';
  }

  // Malformed check: strip well-formed tokens; any leftover brace is unbalanced.
  const stripped = subject.replace(WELL_FORMED_TOKEN, '');
  if (stripped.includes('{') || stripped.includes('}')) {
    return 'Subject has a malformed placeholder — check that every {{ has a matching }}';
  }

  // Data placeholders allowed for this template = known set ∪ tokens already in the default.
  const allowed = new Set<string>([
    ...KNOWN_PLACEHOLDERS,
    ...extractTokens(defaultSubject ?? '').filter(isDataToken)
  ]);

  for (const token of extractTokens(subject)) {
    if (!isDataToken(token)) continue; // TEXT_* is structural, always allowed
    if (!allowed.has(token)) {
      return `Subject contains an unknown placeholder: {{${token}}}`;
    }
  }

  return undefined;
};
